import React, { useState, useEffect } from 'react'
import { Calendar, CheckCircle, Save, RefreshCw } from 'lucide-react'

interface PolicyReviewSettingsProps {
  tenantId: string
}

/**
 * Tenant-Wide Policy Review Settings
 *
 * Configure automatic annual recertification for ALL policies.
 * One setting applies to all policy documents for audit compliance.
 */
const PolicyReviewSettings: React.FC<PolicyReviewSettingsProps> = ({ tenantId }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Form state
  const [enabled, setEnabled] = useState(true)
  const [schedule, setSchedule] = useState<string>('annual')
  const [customDays, setCustomDays] = useState(365)
  const [reviewDay, setReviewDay] = useState('01-01') // Default: January 1
  const [autoStamp, setAutoStamp] = useState(true)
  const [reviewerName, setReviewerName] = useState('')
  const [reviewerEmail, setReviewerEmail] = useState('')
  const [reviewerTitle, setReviewerTitle] = useState('')

  useEffect(() => {
    loadSettings()
  }, [tenantId])

  const loadSettings = async () => {
    setLoading(true)
    setError('')

    try {
      console.log('[PolicyReviewSettings] Loading settings...')
      const response = await fetch('/api/settings-db', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`
        }
      })

      console.log('[PolicyReviewSettings] Load response status:', response.status)

      if (!response.ok) {
        throw new Error('Failed to load settings')
      }

      const data = await response.json()
      const settings = data.success ? data.result : data
      console.log('[PolicyReviewSettings] Loaded settings:', settings)

      // If reviewer fields are empty, try to populate from policy generator
      let name = settings.policyReviewerName || ''
      let email = settings.policyReviewerEmail || ''
      let title = settings.policyReviewerTitle || ''

      if (!name && !email && !title) {
        console.log('[PolicyReviewSettings] Reviewer fields empty, checking for policy generator data...')

        try {
          // Get approver from most recent policy generator session
          const approverResponse = await fetch('/api/policy-generator/latest-approver', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth-token')}`
            }
          })

          if (approverResponse.ok) {
            const approverData = await approverResponse.json()
            if (approverData.name) {
              name = approverData.name
              email = approverData.email || settings.securityEmail || ''
              title = approverData.title || ''
              console.log('[PolicyReviewSettings] Auto-populated from policy generator:', { name, title, email })
            }
          }
        } catch (autoPopError) {
          console.error('[PolicyReviewSettings] Auto-populate failed (non-critical):', autoPopError)
          // Continue with empty fields - this is non-critical
        }
      }

      setEnabled(settings.policyReviewEnabled ?? true)
      setSchedule(settings.policyReviewSchedule || 'annual')
      setCustomDays(settings.policyReviewCustomDays || 365)
      setReviewDay(settings.policyReviewDay || '01-01')
      setAutoStamp(settings.policyReviewAutoStamp ?? true)
      setReviewerName(name)
      setReviewerEmail(email)
      setReviewerTitle(title)

      console.log('[PolicyReviewSettings] Settings state updated')

    } catch (err: any) {
      console.error('[PolicyReviewSettings] Load error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      console.log('[PolicyReviewSettings] Saving settings...')
      console.log('[PolicyReviewSettings] Current form values:', { enabled, schedule, customDays, reviewDay, autoStamp })

      // First, load current settings to merge with
      const loadResponse = await fetch('/api/settings-db', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`
        }
      })

      console.log('[PolicyReviewSettings] Load response status:', loadResponse.status)

      if (!loadResponse.ok) {
        throw new Error('Failed to load current settings')
      }

      const loadData = await loadResponse.json()
      const currentSettings = loadData.success ? loadData.result : loadData
      console.log('[PolicyReviewSettings] Current settings:', currentSettings)

      // Merge policy review settings with existing settings
      const updatedSettings = {
        ...currentSettings,
        policyReviewEnabled: enabled,
        policyReviewSchedule: schedule,
        policyReviewCustomDays: schedule === 'custom' ? customDays : undefined,
        policyReviewDay: schedule === 'annual' ? reviewDay : undefined,
        policyReviewAutoStamp: autoStamp,
        policyReviewerName: reviewerName || undefined,
        policyReviewerEmail: reviewerEmail || undefined,
        policyReviewerTitle: reviewerTitle || undefined
      }

      console.log('[PolicyReviewSettings] Updated settings to save:', updatedSettings)

      console.log('[PolicyReviewSettings] Updated settings to save:', updatedSettings)

      // Save merged settings
      const response = await fetch('/api/settings-db', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`
        },
        body: JSON.stringify(updatedSettings)
      })

      console.log('[PolicyReviewSettings] Save response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('[PolicyReviewSettings] Save error response:', errorText)
        throw new Error(`Failed to save settings: ${errorText}`)
      }

      const result = await response.json()
      console.log('[PolicyReviewSettings] Save result:', result)

      setSuccess('Policy review settings saved! Deploy to Live for scheduled auto-stamp to use these settings.')
      setTimeout(() => setSuccess(''), 5000)

    } catch (err: any) {
      console.error('[PolicyReviewSettings] Save error:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const getNextReviewDate = () => {
    if (!enabled) return 'Disabled'

    const now = new Date()
    let next = new Date()

    switch (schedule) {
      case 'annual':
        const [month, day] = reviewDay.split('-').map(Number)
        next.setFullYear(next.getFullYear() + 1)
        next.setMonth(month - 1)
        next.setDate(day)
        break
      case 'semi-annual':
        next.setMonth(next.getMonth() + 6)
        break
      case 'quarterly':
        next.setMonth(next.getMonth() + 3)
        break
      case 'custom':
        next.setDate(next.getDate() + customDays)
        break
      case 'none':
        return 'Not scheduled'
    }

    return next.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b pb-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Policy Review & Recertification</h3>
        </div>
        <p className="text-sm text-gray-600">
          Configure automatic annual recertification for all policy documents. This ensures audit compliance by maintaining evidence of regular policy review.
        </p>
      </div>

      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <h4 className="font-medium text-gray-900">Enable Policy Review</h4>
          <p className="text-sm text-gray-600">Automatically review and recertify all policies</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {enabled && (
        <>
          {/* Review Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review Schedule <span className="text-red-500">*</span>
            </label>
            <select
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="annual">Annual (Every 12 months)</option>
              <option value="semi-annual">Semi-Annual (Every 6 months)</option>
              <option value="quarterly">Quarterly (Every 3 months)</option>
              <option value="custom">Custom</option>
              <option value="none">None (Manual only)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              This schedule applies to ALL policy documents in your trust center
            </p>
          </div>

          {/* Annual Review Day */}
          {schedule === 'annual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Review Date
              </label>
              <input
                type="text"
                value={reviewDay}
                onChange={(e) => setReviewDay(e.target.value)}
                placeholder="MM-DD (e.g., 01-01)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Policies will be recertified on this date each year (default: January 1st)
              </p>
            </div>
          )}

          {/* Custom Days */}
          {schedule === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Every (days)
              </label>
              <input
                type="number"
                min="1"
                value={customDays}
                onChange={(e) => setCustomDays(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          {/* Reviewer Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reviewer Name (Optional)
              </label>
              <input
                type="text"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                placeholder="e.g., Jane Smith"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Full name for display
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reviewer Title (Optional)
              </label>
              <input
                type="text"
                value={reviewerTitle}
                onChange={(e) => setReviewerTitle(e.target.value)}
                placeholder="e.g., Chief Security Officer"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Job title for display
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reviewer Email (Optional)
              </label>
              <input
                type="email"
                value={reviewerEmail}
                onChange={(e) => setReviewerEmail(e.target.value)}
                placeholder="e.g., security@company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Falls back to security email
              </p>
            </div>
          </div>

          {/* Auto-Stamp */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="autoStamp"
              checked={autoStamp}
              onChange={(e) => setAutoStamp(e.target.checked)}
              className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <label htmlFor="autoStamp" className="text-sm font-medium text-gray-700">
                Enable automatic stamping
              </label>
              <p className="text-xs text-gray-500">
                System will automatically create new version entries on review dates without changing document content
              </p>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Preview</h4>
            <div className="space-y-1 text-sm text-blue-800">
              <p><strong>Schedule:</strong> {schedule === 'annual' ? 'Annual' : schedule === 'semi-annual' ? 'Semi-Annual' : schedule === 'quarterly' ? 'Quarterly' : schedule === 'custom' ? `Every ${customDays} days` : 'Manual only'}</p>
              <p><strong>Next Review:</strong> {getNextReviewDate()}</p>
              <p><strong>Auto-Stamp:</strong> {autoStamp ? 'Enabled' : 'Disabled'}</p>
              <p className="mt-2 text-xs">
                All policies will be automatically recertified on this schedule, creating version history entries for audit compliance.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Audit Compliance Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Audit Compliance</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>✅ SOC 2 / ISO 27001 compliant</p>
          <p>✅ Automatic version history tracking</p>
          <p>✅ No manual intervention required</p>
          <p>✅ Content integrity verified</p>
        </div>
      </div>

      {/* Deployment Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">⚠️ Important: Deployment Required</h4>
        <p className="text-sm text-blue-800">
          After saving these settings, you must <strong>Deploy to Live</strong> for the scheduled auto-stamp job to use them.
          Settings are saved to preview mode and won't affect the daily 2 AM UTC auto-stamp until deployed.
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default PolicyReviewSettings
